import React, { useEffect, useState } from "react";
import "./LeaveSummary.css";
import apiClient from "../utils/apiClient";
import SessionHandler from "../components/SessionHandler";

const LeaveSummary = () => {
  const [breakdown, setBreakdown] = useState([]);
  const [total, setTotal] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* ================================
     Helper: parse "used / total"
  ================================= */
  const parseLeave = (value) => {
    if (!value) return { used: 0, total: 0, remaining: 0 };

    const [used, total] = value.split("/").map((n) => Number(n) || 0);

    return {
      used,
      total,
      remaining: Math.max(0, total - used),
    };
  };

  /* ================================
     Fetch leave summary
  ================================= */
  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await apiClient(
          "/leaves/summary/"
        );

        if (!data) return;

        // ✅ Normalize and reorder halves explicitly
        const orderedBreakdown = [];

        const firstHalf = data.breakdown?.find(
          (h) => h.half?.toLowerCase() === "1st half"
        );

        const secondHalf = data.breakdown?.find(
          (h) => h.half?.toLowerCase() === "2nd half"
        );

        if (firstHalf) orderedBreakdown.push(firstHalf);
        if (secondHalf) orderedBreakdown.push(secondHalf);

        setBreakdown(orderedBreakdown);
        setTotal(data.total || {});
      } catch (err) {
        console.error("Error fetching leave summary:", err);
        setError("Failed to fetch leave summary.");
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  /* ================================
     Render Half Tile
  ================================= */
  const renderHalfTile = (half) => {
    const cl = parseLeave(half.casual_leave);
    const sl = parseLeave(half.sick_leave);
    const el = parseLeave(half.earned_leave);

    return (
      <div className="tile" key={half.half}>
        <h3>{half.half}</h3>

        <p>
          <b>Casual Leave:</b> {cl.used} / {cl.total}
          <span className="remaining"> (Remaining: {cl.remaining})</span>
        </p>

        <p>
          <b>Sick Leave:</b> {sl.used} / {sl.total}
          <span className="remaining"> (Remaining: {sl.remaining})</span>
        </p>

        <p>
          <b>Earned Leave:</b> {el.used} / {el.total}
          <span className="remaining"> (Remaining: {el.remaining})</span>
        </p>
      </div>
    );
  };

  /* ================================
     Render Total Tile
  ================================= */
  const renderTotalTile = () => {
    const cl = parseLeave(total.casual_leave);
    const sl = parseLeave(total.sick_leave);
    const el = parseLeave(total.earned_leave);

    return (
      <div className="tile total-tile">
        <h3>Total (Yearly)</h3>

        <p>
          <b>Casual Leave:</b> {cl.used} / {cl.total}
          <span className="remaining"> (Remaining: {cl.remaining})</span>
        </p>

        <p>
          <b>Sick Leave:</b> {sl.used} / {sl.total}
          <span className="remaining"> (Remaining: {sl.remaining})</span>
        </p>

        <p>
          <b>Earned Leave:</b> {el.used} / {el.total}
          <span className="remaining"> (Remaining: {el.remaining})</span>
        </p>
      </div>
    );
  };

  /* ================================
     UI
  ================================= */
  return (
    <div className="leave-summary-container">
      <h2>Leave Summary</h2>

      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p className="error">{error}</p>
      ) : breakdown.length === 0 ? (
        <p>No leave data available.</p>
      ) : (
        <div className="summary-tiles">
          {breakdown.map(renderHalfTile)}
          {renderTotalTile()}
        </div>
      )}

      <SessionHandler />
    </div>
  );
};

export default LeaveSummary;
